import React, { useState } from 'react';
import './SubscriptionModal.css';

interface SubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  balance?: number;
  monthlyPrice?: number;
  hasSubscription?: boolean;
  onPurchase: () => Promise<void>;
}

const publicUrl = process.env.PUBLIC_URL || '';

const features = [
  {
    icon: `${publicUrl}/icons/svg_icon/camera.svg`,
    title: 'Любое количество камер',
    text: 'Подключайте неограниченное число IP-камер в одной системе.',
  },
  {
    icon: `${publicUrl}/icons/modals/people.svg`,
    title: 'Любое количество аналитик',
    text: 'Используйте нужные AI-сценарии под задачи объекта.',
  },
  {
    icon: null,
    title: '100 МБ памяти',
    text: 'Стабильная работа системы даже на крупных объектах с высокой нагрузкой.',
  },
];

const StorageIcon: React.FC = () => (
  <svg className="sub-modal__icon-svg" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <ellipse cx="24" cy="14" rx="14" ry="5" stroke="currentColor" strokeWidth="2" />
    <path
      d="M10 14v10c0 2.8 6.3 5 14 5s14-2.2 14-5V14"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M10 24v10c0 2.8 6.3 5 14 5s14-2.2 14-5V24"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const formatMoney = (value: number) =>
  value.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  open,
  onClose,
  balance = 0,
  monthlyPrice = 499,
  hasSubscription = false,
  onPurchase,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const canAfford = balance >= monthlyPrice;

  const handleSubscribe = async () => {
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await onPurchase();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Не удалось подключить подписку';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sub-modal" role="dialog" aria-modal="true" aria-labelledby="sub-modal-title">
      <button type="button" className="sub-modal__backdrop" aria-label="Закрыть" onClick={onClose} />
      <div className="sub-modal__dialog">
        <button type="button" className="sub-modal__close" aria-label="Закрыть" onClick={onClose}>
          ×
        </button>

        <h2 id="sub-modal-title" className="sub-modal__title">
          {hasSubscription ? 'Продлить подписку' : 'Единый тариф без ограничений'}
        </h2>

        <div className="sub-modal__balance-row">
          <span className="sub-modal__balance-label">Ваш баланс:</span>
          <span className="sub-modal__balance-value">{formatMoney(balance)} ₽</span>
        </div>

        <ul className="sub-modal__features">
          {features.map((item) => (
            <li key={item.title} className="sub-modal__feature">
              <div className="sub-modal__icon" aria-hidden="true">
                {item.icon ? (
                  <img src={item.icon} alt="" className="sub-modal__icon-img" />
                ) : (
                  <StorageIcon />
                )}
              </div>
              <div className="sub-modal__feature-text">
                <div className="sub-modal__feature-title">{item.title}</div>
                <div className="sub-modal__feature-desc">{item.text}</div>
              </div>
            </li>
          ))}
        </ul>

        {error && (
          <div className="sub-modal__error" role="alert">
            {error}
          </div>
        )}

        {!canAfford && !error && (
          <div className="sub-modal__hint">
            Недостаточно средств. Нужно {formatMoney(monthlyPrice)} ₽, на балансе {formatMoney(balance)} ₽.
          </div>
        )}

        <div className="sub-modal__footer">
          <div className="sub-modal__price">{formatMoney(monthlyPrice)} руб/мес.</div>
          <button
            type="button"
            className="sub-modal__cta"
            onClick={handleSubscribe}
            disabled={loading || !canAfford}
          >
            {loading
              ? 'Подключение…'
              : hasSubscription
                ? 'Продлить на месяц'
                : 'Подключить систему'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
